/**
 * Adds the coming-soon countdown strip to Athos product cards.
 *
 * Athos renders collection, search and recommendation cards client-side, so the theme's Liquid
 * card strip (`coming-soon-card`) never runs there. This fetches the strips from
 * `/collections/all/coming-soon?view=coming-soon` (live Shopify data, same rule as the PDP) and
 * appends them to matching cards, keyed by product handle. The countdown element itself
 * (`coming-soon-countdown.js`, loaded globally) ticks and hides the strip at launch.
 */

const ROOT_SELECTOR = '#athos-templates, [class*="ss__recs__"], .ss__recommendation-bundle';
const CARD_SELECTOR = 'article.ss__custom-result';
const IMAGE_WRAPPER_SELECTOR = '.ss__custom-result__image-wrapper';
const STRIP_ATTRIBUTE = 'data-coming-soon-athos';
const HANDLE_PATTERN = /\/products\/([^/?#]+)/;

/** @returns {Promise<Map<string, HTMLTemplateElement>>} */
async function fetchStrips() {
  const root = window.Shopify?.routes?.root ?? '/';
  const response = await fetch(`${root}collections/all/coming-soon?view=coming-soon`);
  if (!response.ok) return new Map();

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');

  /** @type {Map<string, HTMLTemplateElement>} */
  const strips = new Map();
  for (const template of doc.querySelectorAll('template[data-handle]')) {
    if (template instanceof HTMLTemplateElement && template.dataset.handle) {
      strips.set(template.dataset.handle, template);
    }
  }
  return strips;
}

/**
 * @param {Element} card
 * @returns {string | null}
 */
function cardHandle(card) {
  const link = card.querySelector('a[href*="/products/"]');
  const match = link?.getAttribute('href')?.match(HANDLE_PATTERN);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Adds, keeps or removes the strip on one card. A swatch click swaps the card's link to another
 * product without re-rendering the card, so a strip for a different handle is removed.
 *
 * @param {Element} card
 * @param {Map<string, HTMLTemplateElement>} strips
 */
function syncCard(card, strips) {
  const wrapper = card.querySelector(IMAGE_WRAPPER_SELECTOR);
  if (!wrapper) return;

  const handle = cardHandle(card);
  const existing = wrapper.querySelector(`:scope > [${STRIP_ATTRIBUTE}]`);
  if (existing && existing.getAttribute('data-handle') === handle) return;

  existing?.remove();
  card.removeAttribute('data-coming-soon');

  const template = handle ? strips.get(handle) : undefined;
  if (!template) return;

  const fragment = /** @type {DocumentFragment} */ (document.importNode(template.content, true));
  const strip = fragment.firstElementChild;
  if (!strip) return;

  strip.setAttribute(STRIP_ATTRIBUTE, '');
  strip.setAttribute('data-handle', /** @type {string} */ (handle));
  wrapper.append(strip);
  card.setAttribute('data-coming-soon', '');
}

/**
 * @param {Element} root
 * @param {Map<string, HTMLTemplateElement>} strips
 */
function syncRoot(root, strips) {
  root.querySelectorAll(CARD_SELECTOR).forEach((card) => syncCard(card, strips));
}

/**
 * Athos re-renders cards on sort, filter and page changes, rewrites links on swatch clicks, and
 * renders recommendations only when they scroll into view. Observe each root and re-sync once
 * per frame, skipping mutations caused by our own strips.
 *
 * @param {Element} root
 * @param {Map<string, HTMLTemplateElement>} strips
 */
function observeRoot(root, strips) {
  let frame = 0;

  const observer = new MutationObserver((records) => {
    const relevant = records.some((record) => {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      return !target?.closest(`[${STRIP_ATTRIBUTE}]`) && !isOwnStripChange(record);
    });
    if (!relevant || frame) return;

    frame = requestAnimationFrame(() => {
      frame = 0;
      syncRoot(root, strips);
    });
  });

  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  syncRoot(root, strips);
}

/** @param {MutationRecord} record */
function isOwnStripChange(record) {
  if (record.type !== 'childList') return false;
  const nodes = [...record.addedNodes, ...record.removedNodes];
  return nodes.length > 0 && nodes.every((node) => node instanceof Element && node.hasAttribute(STRIP_ATTRIBUTE));
}

async function init() {
  const roots = document.querySelectorAll(ROOT_SELECTOR);
  if (roots.length === 0) return;

  let strips;
  try {
    strips = await fetchStrips();
  } catch (error) {
    console.error('Coming soon: could not load Athos card strips', error);
    return;
  }
  if (strips.size === 0) return;

  roots.forEach((root) => observeRoot(root, strips));
}

init();
